import { makeAutoObservable, ObservableMap, IObservableArray, observable, autorun, runInAction } from 'mobx';
import { signalSharingStore } from './social-trading/signal-sharing.store';
import { request } from '../services/request';
import { tradeSharingStore } from './social-trading/trade-sharing.store';
import { analysisSharingStore } from './social-trading/analysis-sharing.store';

export type XTickHistoryEntry = {
    open: number;
    close: number;
    high: number;
    low: number;

    relative: number;
    timestamp: string;
};
export type XTickChangeEntry = {
    day: number;
    week: number;
    month: number;
};

export class XTickStore {
    // we store a map of symbols that we are loading or are already loaded
    // this is to prevent duplicating requests
    readonly activeSymbols: ObservableMap<string, boolean> = new ObservableMap();
    readonly history: ObservableMap<string, IObservableArray<XTickHistoryEntry>> = new ObservableMap();
    readonly longHistory: ObservableMap<string, IObservableArray<XTickHistoryEntry>> = new ObservableMap();

    readonly changes: ObservableMap<string, XTickChangeEntry> = new ObservableMap();
    readonly price: ObservableMap<string, number> = new ObservableMap();
    readonly rsi14: ObservableMap<string, number> = new ObservableMap();

    constructor() {
        makeAutoObservable(this);

        autorun(() => {
            signalSharingStore.items.data.map((s) => s.symbol).forEach((symbol) => this.fetchSymbol(symbol));
            tradeSharingStore.items.data.map((s) => s.symbol).forEach((symbol) => this.fetchSymbol(symbol));
            analysisSharingStore.items.data.map((s) => s.symbol).forEach((symbol) => this.fetchSymbol(symbol));
        });
    }

    public async fetchRsi14(symbol: string) {
        const response = await request.get<string>(`/api/v2.0/xtick/${symbol}/rsi`);
        runInAction(() => {
            this.rsi14.set(symbol, parseFloat(response.data));
        });
    }

    public getHistory(symbol: string) {
        const history = xTickStore.history.get(symbol);
        if (!history) {
            return;
        }

        return history;
    }

    public getLongHistory(symbol: string) {
        const longHistory = xTickStore.longHistory.get(symbol);
        if (!longHistory) {
            return;
        }

        return longHistory;
    }

    public getPriceChange(symbol: string, period: keyof XTickChangeEntry) {
        const change = this.changes.get(symbol);

        if (!change) {
            return null;
        }

        return change[period];
    }

    protected async fetchSymbol(symbol: string) {
        if (this.activeSymbols.has(symbol)) {
            return;
        }

        this.activeSymbols.set(symbol, true);

        const changes = await this.fetchChange(symbol);
        const history = await this.fetchHistory(symbol);

        runInAction(() => {
            this.setChanges(symbol, changes);
            this.setHistory(symbol, history);

            // find latest price - get the newest history entry and get it's price
            const currentPrice =
                history.length &&
                history.reduce((a, b) => (new Date(a.timestamp) > new Date(b.timestamp) ? a : b))?.close;
            if (currentPrice) {
                this.price.set(symbol, currentPrice);
            }
        });
    }

    public async fetchLongHistory(symbol: string) {
        const { data } = await request.get<XTickHistoryEntry[]>(`/api/v2.0/xtick/${symbol}/long-history`);

        runInAction(() => {
            const startingValue = data[0]?.close;

            // calculate value relative to first value in history
            this.longHistory.set(
                symbol,
                observable.array(data.map((h) => ({ ...h, relative: h.close - startingValue })))
            );
        });
    }

    protected async fetchHistory(symbol: string) {
        const response = await request.get<XTickHistoryEntry[]>(`/api/v2.0/xtick/${symbol}/history`);

        return response.data;
    }

    protected async fetchChange(symbol: string) {
        const response = await request.get<XTickChangeEntry>(`/api/v2.0/xtick/${symbol}/change`);

        return response.data;
    }

    protected setHistory(symbol: string, history: XTickHistoryEntry[]) {
        const startingValue = history[0]?.close;

        // calculate value relative to first value in history
        this.history.set(symbol, observable.array(history.map((h) => ({ ...h, relative: h.close - startingValue }))));
    }

    protected setChanges(symbol: string, change: XTickChangeEntry) {
        this.changes.set(symbol, change);
    }
}

export const xTickStore = new XTickStore();
